import { Box, Button } from "@mui/material";
import React, { ReactElement } from "react";

import { UseQueryResult } from "@tanstack/react-query";
import {
  IAction,
  IField,
  IPageable,
  ISort,
  Params,
} from "../../../types/table/interfaces.type";
import SearchableTable from "./SearchableTable";

interface CustomTableProps<T> {
  filters: Params<T>;
  fields: IField<T>[];
  fetchFunc:
    | ((params: Params<T>) => UseQueryResult<IPageable<T>, Error>)
    | ((id: string) => UseQueryResult<IPageable<T>, Error>)
    | ((params: Params<T>) => Promise<IPageable<T>>)
    | ((id: string) => Promise<T>);
  actions?: IAction<T>[];
  shadeRow?: (row: T) => boolean;
  shadeBg?: string | ((row: T) => string | undefined);
  shadeColor?: string;
  checkboxSelect?: boolean;
  onRowSelect?: (row: T, checked: boolean) => void;
  isRowSelected?: (row: T) => boolean;
  sortableFields?: (keyof T)[];
  highlightDiff?: boolean;
  /** How highlightDiff compares rows: against the row below ("adjacent",
   *  used by the history view) or against the whole result set ("all"). */
  diffScope?: "adjacent" | "all";
  onRowClick?: (row: T) => void;
  /** Stretch edge-to-edge on mobile by cancelling the page's side padding.
   *  Disable for tables rendered inside dialogs. */
  fullBleedMobile?: boolean;
  /** Sort applied on first render, before the user clicks a column header. */
  initialSort?: ISort<T>;
}

function CustomTable<T>({
  filters,
  fields,
  fetchFunc,
  actions = [],
  shadeRow,
  shadeBg,
  shadeColor,
  checkboxSelect,
  isRowSelected,
  onRowSelect,
  sortableFields,
  highlightDiff = false,
  diffScope = "adjacent",
  onRowClick,
  fullBleedMobile = true,
  initialSort,
}: CustomTableProps<T>) {
  const renderActions = (row: T): ReactElement[] => {
    return actions
      .map((action) => {
        if (action.component) {
          const customComponent = action.component(row);
          if (React.isValidElement(customComponent)) {
            return customComponent;
          }
          return null;
        }
        return (
          <Button
            key={action.id}
            disabled={isDisabled<T>(action, row)}
            onClick={() => action.handler(row)}
            onContextMenu={
              action.onContextMenu
                ? (event: React.MouseEvent) => {
                    event.preventDefault();
                    action.onContextMenu!(event, row);
                  }
                : undefined
            }
            sx={{
              margin: "0 5px",
              width: "min-content",
              height: "min-content",
              minWidth: "0",
            }}
          >
            {typeof action.icon === "function" ? action.icon(row) : action.icon}
          </Button>
        );
      })
      .filter((element): element is ReactElement =>
        React.isValidElement(element)
      );
  };

  return (
    <Box
      sx={{
        width: fullBleedMobile ? { xs: "auto", sm: "100%" } : "100%",
        mx: fullBleedMobile ? { xs: -2, sm: 0 } : 0,
        borderRadius: "10px",
      }}
    >
      <SearchableTable<T>
        filters={filters}
        fields={fields}
        fetchFunc={fetchFunc}
        actions={actions}
        renderActions={renderActions}
        shadeRow={shadeRow}
        shadeBg={shadeBg}
        shadeColor={shadeColor}
        checkboxSelect={checkboxSelect}
        onRowSelect={onRowSelect}
        isRowSelected={isRowSelected}
        sortableFields={sortableFields}
        highlightDiff={highlightDiff}
        diffScope={diffScope}
        onRowClick={onRowClick}
        initialSort={initialSort}
      />
    </Box>
  );
}

export default CustomTable;
function isDisabled<T>(action: IAction<T>, row: T): boolean {
  if (typeof action.disabled == "function") {
    return action.disabled(row);
  }
  return action.disabled === true;
}
